import React, { useCallback, useEffect, useState } from "react";
import { ExternalLink, KeyRound, Power, Save, Trash2 } from "lucide-react";
import AdminPageHeader, { AdminCard, AdminCardBody, AdminCardHeader } from "@/admin/components/AdminPageHeader";

const emptySettings = {
  enabled: false,
  headline_ro: "",
  headline_en: "",
  body_ro: "",
  body_en: "",
  has_bypass_password: false,
};

export default function AdminMaintenance() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [draft, setDraft] = useState(emptySettings);
  const [bypassPassword, setBypassPassword] = useState("");
  const [clearBypass, setClearBypass] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setMsg("");
    try {
      const r = await fetch("/api/admin/maintenance.php", { credentials: "include", cache: "no-store" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok || !j.settings) {
        setMsg(j.detail || "Nu s-a putut încărca setarea.");
        return;
      }
      setDraft(j.settings);
      setBypassPassword("");
      setClearBypass(false);
    } catch {
      setMsg("Eroare de rețea.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async () => {
    setSaving(true);
    setMsg("");
    try {
      const payload = { ...draft };
      if (clearBypass) {
        payload.clear_bypass_password = true;
      } else if (bypassPassword.trim()) {
        payload.bypass_password = bypassPassword.trim();
      }

      const r = await fetch("/api/admin/maintenance.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Salvare eșuată.");
        return;
      }
      setDraft(j.settings);
      setBypassPassword("");
      setClearBypass(false);
      setMsg("Salvat. Vizitatorii văd pagina de mentenanță doar dacă comutatorul este ON.");
    } catch {
      setMsg("Eroare de rețea.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = () => setDraft((d) => ({ ...d, enabled: !d.enabled }));

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl py-16 text-center text-sm text-[#3e2723]/55">Se încarcă…</div>
    );
  }

  const bypassActive = draft.has_bypass_password && !clearBypass;

  return (
    <div className="mx-auto min-w-0 max-w-3xl space-y-6 overflow-x-clip pb-8">
      <AdminPageHeader
        eyebrow="Site public"
        title="Mod mentenanță"
        description={
          <>
            Când este activ, vizitatorii văd o pagină elegantă în locul site-ului. Panoul{" "}
            <strong className="font-semibold text-[#3e2723]/85">/admin</strong> rămâne accesibil ca să poți opri
            mentenanța oricând.
          </>
        }
      />

      <AdminCard>
        <AdminCardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span
                className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                  draft.enabled ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
                }`}
              >
                <Power className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold text-[#3e2723]">
                  {draft.enabled ? "Mentenanță ACTIVĂ" : "Mentenanță oprită"}
                </p>
                <p className="text-xs text-[#3e2723]/55">
                  {draft.enabled ? "Publicul vede doar ecranul de mentenanță." : "Site-ul este vizibil normal."}
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={draft.enabled}
              onClick={toggle}
              className={`relative h-10 w-[4.5rem] shrink-0 rounded-full transition-colors ${
                draft.enabled ? "bg-rose-500" : "bg-[#3e2723]/15"
              }`}
            >
              <span
                className={`absolute top-1 h-8 w-8 rounded-full bg-white shadow transition-transform ${
                  draft.enabled ? "left-[calc(100%-2.25rem)]" : "left-1"
                }`}
              />
              <span className="sr-only">Comută mod mentenanță</span>
            </button>
          </div>
        </AdminCardHeader>

        <AdminCardBody className="space-y-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#3e2723]/45">Mesaje pe pagina publică</p>

          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-[#3e2723]/65">Titlu (RO)</span>
            <input
              value={draft.headline_ro}
              onChange={(e) => setDraft((d) => ({ ...d, headline_ro: e.target.value }))}
              className="w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 py-2.5 text-sm text-[#3e2723] outline-none focus:ring-2 focus:ring-[#3e2723]/10"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-[#3e2723]/65">Titlu (EN)</span>
            <input
              value={draft.headline_en}
              onChange={(e) => setDraft((d) => ({ ...d, headline_en: e.target.value }))}
              className="w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 py-2.5 text-sm text-[#3e2723] outline-none focus:ring-2 focus:ring-[#3e2723]/10"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-[#3e2723]/65">Text (RO)</span>
            <textarea
              rows={3}
              value={draft.body_ro}
              onChange={(e) => setDraft((d) => ({ ...d, body_ro: e.target.value }))}
              className="w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 py-2.5 text-sm text-[#3e2723] outline-none focus:ring-2 focus:ring-[#3e2723]/10"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-[#3e2723]/65">Text (EN)</span>
            <textarea
              rows={3}
              value={draft.body_en}
              onChange={(e) => setDraft((d) => ({ ...d, body_en: e.target.value }))}
              className="w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 py-2.5 text-sm text-[#3e2723] outline-none focus:ring-2 focus:ring-[#3e2723]/10"
            />
          </label>
        </AdminCardBody>

        <div className="border-t border-[#3e2723]/8 bg-[#faf8f8]/50 px-5 py-5 sm:px-6">
          <div className="flex flex-wrap items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fbd9e5]/80 text-[#3e2723]">
              <KeyRound className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-[#3e2723]">Parolă acces previzualizare</p>
                {bypassActive ? (
                  <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                    Activă
                  </span>
                ) : (
                  <span className="rounded-full bg-[#3e2723]/8 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#3e2723]/50">
                    Neconfigurată
                  </span>
                )}
              </div>
              <p className="mt-1 max-w-xl text-xs leading-relaxed text-[#3e2723]/55">
                Ca la Shopify sau WordPress: persoanele cărora le trimiți parola pot vedea site-ul chiar dacă
                mentenanța este activă. Cookie-ul de acces durează 14 zile.
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="block min-w-0 flex-1">
              <span className="mb-1.5 block text-xs font-medium text-[#3e2723]/65">
                {bypassActive ? "Parolă nouă (opțional)" : "Setează parola"}
              </span>
              <input
                type="password"
                value={bypassPassword}
                onChange={(e) => {
                  setBypassPassword(e.target.value);
                  if (clearBypass) setClearBypass(false);
                }}
                autoComplete="new-password"
                placeholder={bypassActive ? "Lasă gol pentru a păstra parola curentă" : "Ex: preview2026"}
                className="w-full rounded-xl border border-[#3e2723]/12 bg-white px-3 py-2.5 text-sm text-[#3e2723] outline-none focus:ring-2 focus:ring-[#3e2723]/10"
              />
            </label>
            {bypassActive ? (
              <button
                type="button"
                onClick={() => {
                  setClearBypass(true);
                  setBypassPassword("");
                }}
                className="inline-flex h-[42px] shrink-0 items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 text-xs font-semibold text-rose-700 transition hover:bg-rose-100"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Elimină parola
              </button>
            ) : null}
          </div>
          {clearBypass ? (
            <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Parola va fi eliminată la salvare. Accesul cu parolă va fi dezactivat.
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#3e2723]/8 px-5 py-4 sm:px-6">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-xs font-semibold text-[#3e2723]/65 underline decoration-[#3e2723]/20 underline-offset-4 hover:text-[#3e2723]"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Preview site (filă nouă)
          </a>
          <button
            type="button"
            disabled={saving}
            onClick={onSave}
            className="inline-flex items-center gap-2 rounded-full bg-[#3e2723] px-6 py-2.5 text-sm font-semibold text-[#fbd9e5] shadow-sm transition hover:bg-[#2a1a17] disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? "Se salvează…" : "Salvează setările"}
          </button>
        </div>
      </AdminCard>

      {msg ? (
        <p className="rounded-xl border border-[#3e2723]/10 bg-white px-4 py-3 text-sm text-[#3e2723]/80">{msg}</p>
      ) : null}

      <p className="text-xs leading-relaxed text-[#3e2723]/50">
        Setările sunt salvate în <code className="rounded bg-white px-1 py-0.5 text-[11px]">api/data/maintenance.json</code>{" "}
        pe server.
      </p>
    </div>
  );
}
